//  ----------------------------------------------------------------------------
//  Dependencies
//  ----------------------------------------------------------------------------
import { ArrowLeftOutlined, ArrowRightOutlined, EyeInvisibleOutlined, EyeOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Assignment, Close, DragIndicator, Extension } from '@mui/icons-material';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
//  -- Components ---------------------
import { Col, Dropdown, Popover, Row } from 'antd';
import { filter, get, map } from "lodash";
import moment from 'moment';
import { lighten } from 'polished';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Birdimg, GradCalendar } from "utils/assets/images";
//  -- Constants ----------------------
import { DownOutlined } from '@ant-design/icons';
import { Modal } from '@mui/material';
import { useOrientation } from 'components/hooks';
import Loading from 'components/Loading';
import useMobileDeviceDetection from 'containers/chat/hooks';
import { Img } from 'react-image';
import { useCurrentClass, useCurrentClassBook } from 'store/classes/selectors';
import { useResources, useVideos } from 'store/resources/selectors';
import COLOR from 'utils/constants/color';
import { getBookDisplayName, imageFetch, imageFetchFromPodcast, isBookAllowed, isVideo } from 'utils/lib/ebookHelper';
import { useUserAllowedBooks } from 'store/books/selectors';
import { getCoverForAssignment } from 'utils/methods/functions';
import { usePageCover } from 'components/hooks/pageCover/pageCover';
import GradebookPopoverContent from 'components/UI/PopoverConf/GradebookPopover';
import { useCurrentUser } from 'store/users/selectors';
import SentryWrap from 'components/Sentry';
import { getDateOnlyRangeBounds } from 'utils/methods/gradebookDateRange';
import { getAssignmentCalendarSortKey, sortAssignmentsByCalendarOrder } from 'utils/methods/calendarAssignmentOrder';
import { useClassActivities } from 'store/classActivities/selectors';

const ORDER_BY = [
    { key: "due_date", value: "Due Date" },
    { key: "start_date", value: "Start Date" },
    { key: "late_submission", value: "Late Date" }
]

//  ----------------------------------------------------------------------------
//  Styles
//  ----------------------------------------------------------------------------
const AssignmentContainer = styled.div`
    border: 1px solid #F4F5F7;
    height: 100%;
    min-height: 160px;
    padding: 8px 4px;
`;

const Button = styled.button`
    background-color: transparent;
    border: none;
    color: ${COLOR.green200};
    cursor: pointer;
    outline: none;
    transition: all 300ms ease;

    &:hover {
        color: ${COLOR.green}
    }
`;
const PaddedBtn = styled.button`
    background-color: transparent;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    outline: none;
    padding: 8px;
    text-align: left;
    width: 100%;
`;
const ContainerDay = styled.div`
    border-bottom: 4px solid #03989e82;
    border-right: 2px solid #03989e82;
    border-left: 2px solid #03989e82;
    flex: 1;
    margin-bottom: 16px;
    margin-right: 8px;
    min-width: 124px;

    &:last-child {
        margin-right: 0px;
    }
`;

const ControlRow = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
    padding:10px 10px;
    border-radius: 8px;
    cursor:pointer;
    background:rgb(243,249,255);
    border:.5px solid lightgray;
`;

const AllHeader = styled.div`
    align-items: center;
    display: flex;
    justify-content: space-between;
    width: 100%;
     @media (max-width: 570px) {
       display:block;
       text-align: center;
     }
`;

const CustomRow = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
`;

const Date = styled.span`
    color: #fff;
    display: block;
    font-size: 11px;
    line-height: 10px;
    margin-bottom: 6px;
`;

const DayOfWeek = styled.span`
    background-color: #03989e82;
    border-radius: 2px;
    color: ${COLOR.black};
    display: block;
    font-size: 16px;
    font-weight: 400;
    line-height: 16px;
    padding: 12px 12px;
`;

const Header = styled.span`
    color: ${COLOR.black600};
    display: inline-block;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
`;

const HeaderBold = styled(Header)`
    color: ${COLOR.black100};
`;

const Item = styled.div`
    align-items: center;
    background-color: ${p => p.deactivated ? COLOR.redishpink : p.disabled ? COLOR.green900 : (p.past ? lighten(0.4, 'red') : COLOR.white)};
    border: 1px dashed ${p => (p.deactivated || p.disabled || p.past) ? COLOR.white : COLOR.black700};
    border-radius: 2px;
    color: ${p => (p.deactivated || p.disabled || p.past) ? COLOR.white : COLOR.black200};
    cursor: ${p => p.past ? 'not-allowed' : 'pointer'};
    display: flex;
    flex-direction: row;
    font-size: 13px;
    line-height: 15px;
    margin-bottom: 4px;
    position: relative;
    text-transform: capitalize;
    transition: all 450ms ease;
    width: 100%;

    @media (min-width: 1000px) {
        &:hover {
            background-color: ${p => !p.past && '#f9f9f9'};
            color:${COLOR.green};
            box-shadow: ${p => !p.past && '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 9px 28px 8px rgba(0, 0, 0, 0.05)'};
        }
    }
`

const Wrapper = styled.div`
    width: 100%;
`;
const CalendarDay = styled.div`
    background-color:${p => p.isToday ? COLOR.orange100 : "#5f4c82"};
    border:3px solid #c5a1cf;
    border-radius: 15px;
    padding: 10px;
    position: relative;
    flex: 1;
    margin-bottom: 16px;
    margin-right: 8px;
    min-width: 124px;
    min-height: 180px;
    &:last-child {
        margin-right: 0px;
    }
    @media screen and (max-width: 992px) {
        min-width: 150px;
    }
    @media screen and (min-width: 360px) and (max-width: 420px) {
        min-width: 160px;
    }
    @media screen and (min-width: 522px) and (max-width: 580px) {
        min-width: 165px;
    }
    @media screen and (min-width: 680px) and (max-width: 690px) {
        min-width: 165px;
    }
    @media screen and (min-width: 690px) and (max-width: 730px) {
        min-width: 165px;
    }
    @media screen and (min-width: 846px) and (max-width: 930px) {
        min-width: 165px;
    }
    @media screen and (min-width: 992px) and (max-width: 1260px) {
        min-width: 165px;
    }
`;
const HeaderRow = styled.div`
    flex-direction: row;
    display: flex;
`
// const CalendarIcon = styled.div`
//     flex: 1;
//     background-image: url(${Calendarimg});
//     background-repeat: no-repeat;
//     background-size: 200% 200%;
//     background-size: 40px 40px;
//     height: 25px;
//     width: 30px;
//     background-position: center;
// `
const GradCalendarSec = styled.div`
    display:flex;
    align-items: center;
    justify-content: space-between;

`
const Birtdimg = styled.img`
    height: 180px;
    width: 220px;
    position: absolute;
    top: -77px;
    left: -110px;
    z-index: 0;
    user-select: none;
    @media screen and (max-width: 600px) {
       left:-100px;
    }
`
const DayTxt = styled.div`
    flex: 6;
    color: white;
    font-size: 20px;
    `;
const ItemTab = styled.button`
    height:100%;
    background: none;
    outline: none;
    border: none;
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 0;
    padding: 8px;
    cursor: pointer;
`;
const ItemTab2 = styled.button`
    height:100%;
    background: none;
    outline: none;
    border: none;
    display: flex;
    align-items: center;
    padding:8px;
    cursor: pointer;
`;
const Image = styled.img`
    border: 1px solid ${COLOR.black800};
    width: 100%;
    height: auto;
`;
const ImageModal = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    height: 90vh;
    margin-top:20px;
`;
const StyledImg = styled(Img)`
  width: ${({ imgType }) => (imgType ? "70vw" : "100%")};
  height: 100%;
  max-width: 90vw;
  max-height: 80vh;
  object-fit: contain;

  @media (min-width: 1367px) {
    width: ${({ imgType }) => (imgType ? "34vw" : "100%")};
  }

  @media (max-width: 600px) {
    max-height: 70vh;
  }
`;

const ModelDescRow = styled.div`
    font-size: 12px;
    font-weight: bold;
    margin-bottom: 10px;
    position:absolute;
    background:#E7E8EA;
    width:100%;
    display:flex;
    align-items:center;
    justify-content:center;
    height:35px;
    border-bottom:.5px solid lightgray;
    margin-top:-35px;
`;
const CloseBtn = styled.div`
    background-color: rgb(204, 80, 71);
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    position: absolute;
    top: 10px;
    right: 10px;
    color: white;
    z-index: 2;
    position:absolute;
    top:-45px;
    right:-13px;
`;
const AssignmentNameText = styled.span`
    display: block;
    flex: 1;
    min-width: 0;
    // width: 100px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
`;
const AnimatedContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    overflow: hidden; // Hide content when collapsed
    transition: all 0.5s ease-in-out;  // Apply smooth transition for max-height, opacity, and padding

    // When isExpand is true, set the max-height to a large value (e.g., 1000px)
    max-height: ${props => props.isExpand ? '5000px' : '0'};  // Adjust max-height to ensure smooth collapse
    opacity: ${props => props.isExpand ? 1 : 0};  // Fade in/out when expanded/collapsed

    // Responsive adjustment for layout
    @media screen and (max-width: 768px) {
        flex-direction: column;
    }
`;
const ImageStoryTitle = styled.div`
    position: absolute;
    top: 54.5%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #fff;
    text-align: center;
    font-weight: bold;
    font-size: 32px;
    z-index: 1;
`;

const DragGrip = styled.span`
    align-items: center;
    color: ${COLOR.black600};
    cursor: grab;
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    padding: 4px 2px 4px 6px;
    touch-action: none;
    &:active {
        cursor: grabbing;
    }
`;


//  ----------------------------------------------------------------------------
//  Feature
//  ----------------------------------------------------------------------------
//
//  -- Description
//  Calendar of events for quick view
//
//  -- Props
//  ...
//
const getDisplayDateByPreference = (x, datePrefrences) => {
    if (datePrefrences === 'start_date') return get(x, 'scheduleAt');
    if (datePrefrences === 'late_submission') return get(x, 'lateDate') || get(x, 'dueDate');
    return get(x, 'dueDate');
};
const getCalendarSequence = (assignment) => {
    const v = Number(get(assignment, 'meta_data.sequence', null));
    return Number.isFinite(v) && v > 0 ? v : Number.MAX_SAFE_INTEGER;
};

const SortableGradebookAssignmentRow = ({
    sortableId,
    x,
    selectedAssignment,
    booksAllList,
    currentUser,
    selectAssignment,
    viewInfo,
}) => {
    const isWorksheet = x.type === 'worksheet';
    const id = isWorksheet
        ? get(x, "session_id", get(x, "id", ""))
        : get(x, "activityId", get(x, "quizId", ""));
    const isActive = get(x, "active", true)
    const displayName = get(x, 'displayName', get(x, 'data.displayName', ''));
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.88 : 1,
        zIndex: isDragging ? 20 : undefined,
    };
    return (
        <Popover placement="rightTop" content={<GradebookPopoverContent booksAllList={booksAllList} x={x} currentUser={currentUser} />} title="Assignment Info" trigger="hover">
            <div ref={setNodeRef} {...attributes} style={{ ...style, width: '100%' }}>
                <Item disabled={selectedAssignment === id} deactivated={!isActive} >
                   <div className='flex items-center w-full justify-between' style={{paddingRight: "8px"}}>
                     <>
                        <DragGrip {...listeners}>
                            <DragIndicator style={{ fontSize: 18 }} />
                        </DragGrip>
                        <ItemTab
                            type="button"
                            onClick={() => selectAssignment(x)}
                        >
                            {get(x, "data.typeId") === 'activity'
                                ? <Extension style={{ height: '18px', marginRight: '4px' }} />
                                : <Assignment style={{ height: '18px', marginRight: '4px' }} />
                            }
                            <AssignmentNameText className='text-wrap'>
                                {displayName}
                            </AssignmentNameText>
                            {!isActive ? "  (Deactivated)" : null}
                        </ItemTab>
                     </>
                     <ItemTab2 type="button" className='infoIconModal' onClick={() => viewInfo(x)}>
                        <InfoCircleFilled />
                    </ItemTab2>
                   </div>
                </Item>
            </div>
        </Popover>
    );
};

const GradeBookCalendar = ({
    assignments,
    onSelect,
    booksAllList,
    onDateChange,
    updateDateLoader,
    setDatePrefrences,
    datePrefrences,
    selectedAssignment,
    dateRange,
    onCalendarDayOrderChange,
}) => {
    const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const [selectedDay, setSelectedDay] = useState(moment());
    const weekStart = selectedDay.clone().startOf('isoWeek');
    const [modalOpen, setModalState] = useState(false);
    const [activeAssignment, setActiveAssignment] = useState(null)
    const resources = useResources()
    const videos = useVideos()
    const landscapeOrientation = useOrientation();
    const deviceType = useMobileDeviceDetection();
    const currentClass = useCurrentClass()
    const allowed_books = useCurrentClassBook();
    const userAllowedBooks = useUserAllowedBooks()
    const currentUser = useCurrentUser();
    const selectedLabel = (ORDER_BY.find(o => o.key === datePrefrences)?.value) || "Due Date";
    const [isExpand, setIsExpand] = useState(() => {
        const saved = localStorage.getItem('isExpandCalendar');
        return saved !== null ? saved === 'true' : true;
    });
    const [optimisticDayOrderMap, setOptimisticDayOrderMap] = useState({});

    useEffect(() => {
        localStorage.setItem('isExpandCalendar', isExpand);
    }, [isExpand]);
    const assignmentData = useMemo(() => {
        const list = Array.isArray(assignments) ? assignments : [];
        const archivedBooksSet = new Set();
        const archived_books = get(currentClass, "archived_books", []);
        archived_books.forEach(abk => {
            archivedBooksSet.add(abk.bookId);
            archivedBooksSet.add(abk.chapter);
        });

        let filtered = filter(list, k => {
            const chapter = get(k, "data.bookId") || get(k, "data.chapter")
            const type = get(k, "type", "");
            return isBookAllowed(userAllowedBooks?.other_permissions?.resourses, k, type, allowed_books);
        });
        const rangeBounds = getDateOnlyRangeBounds(dateRange);
        if (rangeBounds) {
            const startMs = rangeBounds.start.getTime();
            const endMs = rangeBounds.end.getTime();
            filtered = filter(filtered, item => {
                const displayDate = getDisplayDateByPreference(item, datePrefrences);
                if (!displayDate) return false;
                const t = new window.Date(displayDate).getTime();
                return t >= startMs && t <= endMs;
            });
        }
        return filtered;
    }, [assignments, currentClass, allowed_books, dateRange, datePrefrences]);

    const assignmentsByDay = useMemo(() => {
        const week = selectedDay.clone().startOf('isoWeek');
        const out = {};
        for (let i = 0; i <= 6; i++) {
            const day = week.clone().add(i, 'days');
            const dayKey = day.format('YYYY-MM-DD');
            const items = [];
            assignmentData.forEach((x) => {
                if (!x || x.type === 'game' || x.type === 'debate' || x.type === 'bingo') return;
                let displayDate;
                if (datePrefrences === 'start_date') {
                    displayDate = x.scheduleAt;
                } else if (datePrefrences === 'late_submission') {
                    displayDate = x.lateDate || x.dueDate;
                } else {
                    displayDate = x.dueDate;
                }
                if (moment(displayDate).format('MDYY') === day.format('MDYY')) {
                    items.push(x);
                }
            });
            const sortedBySequence = [...items].sort((a, b) => getCalendarSequence(a) - getCalendarSequence(b));
            const orderedKeys = optimisticDayOrderMap?.[dayKey];
            out[dayKey] = sortAssignmentsByCalendarOrder(sortedBySequence, orderedKeys);
        }
        return out;
    }, [assignmentData, optimisticDayOrderMap, datePrefrences, selectedDay]);

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
    const assignmentsByDayRef = useRef(assignmentsByDay);
    assignmentsByDayRef.current = assignmentsByDay;

    const handleCalendarDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || !onCalendarDayOrderChange) return;
        if (active.id === over.id) return;
        const sid = String(active.id);
        const oid = String(over.id);
        const sep = '::';
        if (!sid.includes(sep) || !oid.includes(sep)) return;
        const dayKeyA = sid.split(sep)[0];
        const dayKeyB = oid.split(sep)[0];
        if (dayKeyA !== dayKeyB) return;
        const list = assignmentsByDayRef.current[dayKeyA];
        if (!Array.isArray(list) || list.length < 2) return;
        const oldIndex = list.findIndex((x) => `${dayKeyA}::${getAssignmentCalendarSortKey(x)}` === sid);
        const newIndex = list.findIndex((x) => `${dayKeyB}::${getAssignmentCalendarSortKey(x)}` === oid);
        if (oldIndex < 0 || newIndex < 0) return;
        const next = arrayMove(list, oldIndex, newIndex);
        const nextOrderKeys = next.map(getAssignmentCalendarSortKey);
        const prevOrderKeys = optimisticDayOrderMap?.[dayKeyA];
        setOptimisticDayOrderMap(prev => ({
            ...(prev || {}),
            [dayKeyA]: nextOrderKeys
        }));
        Promise.resolve(onCalendarDayOrderChange(dayKeyA, nextOrderKeys)).catch(() => {
            setOptimisticDayOrderMap(prev => ({
                ...(prev || {}),
                [dayKeyA]: prevOrderKeys
            }));
        });
    }, [onCalendarDayOrderChange, optimisticDayOrderMap]);

    const generateDays = () => {
        const days = [];

        for (let i = 0; i <= 6; i++) {
            days.push(moment(weekStart).add(i, 'days'))
        }

        return days;
    };

    const orderNav = () => {
        const items = ORDER_BY.map((item) => ({
            key: item.key,
            label: (
                <PaddedBtn onClick={() => {
                    setDatePrefrences(item.key)
                    onDateChange(item.key)
                }}>
                    {item.value}
                </PaddedBtn>
            )
        }));

        return { items };
    };

    const WEEK = generateDays();


    const selectAssignment = (x) => {
        const isWorksheet = x.type === 'worksheet';
        const id = isWorksheet 
            ? get(x, "session_id", get(x, "id", ""))
            : get(x, "activityId", get(x, "quizId", ""));
        const isSelected = selectedAssignment === id || String(selectedAssignment) === String(id);
        if (isSelected) {
            onSelect(null);
        } else {
            onSelect(id);
        }
    };

    const viewInfo = (x) => {
        setActiveAssignment(x)
        setModalState(true)
    }
    const closeViewinfo = () => {
        setModalState(false)
        setActiveAssignment(null)
    }
    const is_soy_story = useMemo(() => activeAssignment && ((typeof activeAssignment.storyInstruction == "string" && activeAssignment.storyInstruction.length > 0) || (activeAssignment?.data && typeof activeAssignment?.data.storyInstruction == "string" && activeAssignment?.data.storyInstruction.length > 0)), [activeAssignment])
    const is_article_level = useMemo(() => activeAssignment && ((typeof activeAssignment.article_level_id == "string" && activeAssignment.article_level_id.length > 0) || (activeAssignment?.data && typeof activeAssignment?.data.article_level_id == "string" && activeAssignment?.data.article_level_id.length > 0)), [activeAssignment])
    const is_worksheet = useMemo(() => activeAssignment && activeAssignment.type === 'worksheet' || activeAssignment?.data?.type === 'worksheet' || activeAssignment?.data?.assignmentType === 'worksheet', [activeAssignment])
const pageCover = usePageCover({ assignmentData: activeAssignment?.data });    
const modalSrc = useCallback(() => {
        const cover_img_ext = pageCover ? pageCover : getCoverForAssignment(activeAssignment)
        if (cover_img_ext) return cover_img_ext;
        const chapter_id = get(activeAssignment, "data.chapter") || get(activeAssignment, "assignment.chapter")
        const page_id = get(activeAssignment, "data.page") || get(activeAssignment, "assignment.page")
        return isVideo(page_id, resources) ? imageFetchFromPodcast(page_id, resources) : imageFetch(booksAllList, chapter_id, page_id)
    }, [activeAssignment, resources, booksAllList, pageCover,is_soy_story,is_worksheet,is_article_level]);
    return (
    <SentryWrap>
        <Wrapper>
            <Modal open={modalOpen} onClose={closeViewinfo} className="333 imgModl">
                <ImageModal>
                    <div className="mdlcont">
                        <CloseBtn onClick={closeViewinfo}>
                            <Close className="closeIcns" />
                        </CloseBtn>
                        <ModelDescRow>
                            {getBookDisplayName(booksAllList, get(activeAssignment, "data.chapter"), true, videos, resources)}
                        </ModelDescRow>

                        <StyledImg imgType={isVideo(get(activeAssignment, "data.page"), resources)} src={modalSrc()} loader={<Loading />} />
                         {Boolean(is_soy_story || is_worksheet || is_article_level) && (
                            <label className='storyTitleCstm 444'>{activeAssignment && get(activeAssignment, 'displayName', get(activeAssignment, 'data.displayName', ''))}</label>
                         )}
                    </div>
                </ImageModal>
            </Modal>
            <Row gutter={[0, 16]}>
                <Col span={24}>
                    <ControlRow>
                        <AllHeader>
                            <Header style={{ marginLeft: 30 }}>Week <HeaderBold>{WEEK[0].format('D MMMM')} - {WEEK[6].format('D MMMM')}</HeaderBold>
                                {isExpand ? <EyeOutlined style={{ marginLeft: 15, fontSize: 20 }} onClick={() => setIsExpand(!isExpand)} /> : <EyeInvisibleOutlined style={{ marginLeft: 15, fontSize: 20 }} onClick={() => setIsExpand(!isExpand)} />}
                            </Header>
                            <Dropdown menu={orderNav()} placement="bottomLeft" trigger={['click']}>
                                <Button className='viewByBtn' style={{ marginRight: '16px',display:"flex", flexDirection: "row", alignItems: "center" }}
                                    disabled={updateDateLoader}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}>
                                    {updateDateLoader && <Loading size={15} />}
                                    View By ({selectedLabel})<DownOutlined style={{ marginLeft:"8px", fontSize:"15px" }} />
                                </Button>
                            </Dropdown>
                        </AllHeader>
                        <CustomRow>
                            <ArrowLeftOutlined
                                onClick={() => setSelectedDay(moment(selectedDay).subtract(7, 'days'))}
                                style={{ color: COLOR.black500, cursor: 'pointer', marginLeft: '16px' }}
                            />
                            <ArrowRightOutlined
                                onClick={() => setSelectedDay(moment(selectedDay).add(7, 'days'))}
                                style={{ color: COLOR.black500, cursor: 'pointer', marginLeft: '8px' }}
                            />
                        </CustomRow>
                    </ControlRow>
                </Col>
            </Row>
            <div style={{ position: 'relative' }}>
             <AnimatedContainer isExpand={isExpand} className='111 CalforIpad' style={{ marginTop: 16, flexDirection: landscapeOrientation && window.innerWidth > 768 ? 'row' : deviceType ? "column" : 'row' }}>
                <Birtdimg src={Birdimg} />
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCalendarDragEnd}>
                    {WEEK.map((day) => {
                        const dayKey = day.format('YYYY-MM-DD');
                        const dayItems = assignmentsByDay[dayKey] || [];
                        const sortableIds = dayItems.map(x => `${dayKey}::${getAssignmentCalendarSortKey(x)}`);
                        return (
                            <CalendarDay isToday={moment().isSame(day, 'day')} key={day.unix()} style={{ width: deviceType ? '100%' : 'unset' }}>
                                <GradCalendarSec>
                                    <Date>{day.format('D MMMM')}</Date>
                                    <img src={GradCalendar} style={{ width: "20px" }} />
                                </GradCalendarSec>
                                <HeaderRow>
                                    <DayTxt className='text-wrap'>{DAYS[day.day()]}</DayTxt>
                                </HeaderRow>
                                <AssignmentContainer style={{ paddingLeft: 0, paddingRight: 0, borderWidth: 0, minHeight: 0 }}>
                                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                                        {map(dayItems, x => (
                                            <SortableGradebookAssignmentRow
                                                key={`${dayKey}::${getAssignmentCalendarSortKey(x)}`}
                                                sortableId={`${dayKey}::${getAssignmentCalendarSortKey(x)}`}
                                                x={x}
                                                selectedAssignment={selectedAssignment}
                                                booksAllList={booksAllList}
                                                currentUser={currentUser}
                                                selectAssignment={selectAssignment}
                                                viewInfo={viewInfo}
                                            />
                                        ))}
                                    </SortableContext>
                                </AssignmentContainer>
                            </CalendarDay>);
                    })}
                    </DndContext>
            </AnimatedContainer>
            </div> 
            
        </Wrapper>
    </SentryWrap>
    );
};


export default GradeBookCalendar;